/**
 * Almacen externo minimo para el estado de conexion. No es Zustand a proposito: el
 * contrato del maestro reserva Zustand para estado de negocio genuinamente compartido y
 * espera tres almacenes como maximo en toda la aplicacion; esto es un espejo de eventos
 * del navegador, mas angosto que eso, y `useSyncExternalStore` lo sirve sin sumar a ese
 * presupuesto ni agregar una dependencia.
 *
 * Este modulo NO es un hook y por eso su nombre va en español. El hook que lo consume si
 * tiene que llamarse `useEstadoConexion`, que es la adjudicacion 49.
 */

export type EstadoConexion = {
  readonly enLinea: boolean;
  readonly fallosSeguidos: number;
};

/**
 * EL SNAPSHOT SE GUARDA Y SE REUSA, no se construye en cada llamada. El plan escribia
 * `return { enLinea, fallosSeguidos }` dentro de `obtenerSnapshot`, o sea un objeto nuevo
 * cada vez: React compara el resultado con `Object.is`, ve una identidad distinta sin que
 * nada haya cambiado y vuelve a renderizar para siempre. El sintoma es
 * "The result of getSnapshot should be cached to avoid an infinite loop", y lo peor es
 * que las cifras que se ven en pantalla son correctas hasta el momento en que se cuelga.
 */
let snapshot: EstadoConexion = {
  enLinea: typeof navigator !== 'undefined' ? navigator.onLine : true,
  fallosSeguidos: 0,
};

const oyentes = new Set<() => void>();

function notificar() {
  for (const oyente of oyentes) oyente();
}

/** Solo crea un objeto nuevo —y solo notifica— si de verdad cambio algo. */
function fijar(cambios: Partial<EstadoConexion>) {
  const siguiente: EstadoConexion = { ...snapshot, ...cambios };
  if (siguiente.enLinea === snapshot.enLinea
      && siguiente.fallosSeguidos === snapshot.fallosSeguidos) {
    return;
  }
  snapshot = siguiente;
  notificar();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { fijar({ enLinea: true }); });
  window.addEventListener('offline', () => { fijar({ enLinea: false }); });
}

export function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => { oyentes.delete(oyente); };
}

export function obtenerSnapshot(): EstadoConexion {
  return snapshot;
}

/** Solo cuenta un fallo de RED, nunca un rechazo de negocio con la conexion intacta. */
export function registrarFalloDeRed() {
  fijar({ fallosSeguidos: snapshot.fallosSeguidos + 1 });
}

/** Una escritura que si llego reinicia el contador: solo importan los fallos SEGUIDOS. */
export function registrarExitoDeRed() {
  fijar({ fallosSeguidos: 0 });
}
